import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, TextField, Typography, alpha, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import PasswordStrengthMeter from '../component/common/PasswordStrengthMeter';
import { httpClient } from '../common/httpclient';
import { standaloneFieldSx } from '../common/fieldStyles';
import MobileChangePassword from '../component/auth/MobileChangePassword';
import { useIsMobileShell } from '../hooks/useIsMobileShell';

const ChangePasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .min(4, 'Senha deve ter pelo menos 4 caracteres')
    .required('Nova senha é obrigatória'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Senhas devem ser iguais')
    .required('Confirmação de senha é obrigatória')
});

/**
 * Redefinir senha — DESIGN-SYSTEM.md item 1 (Autenticação & telas públicas).
 * Fluxo público via token na URL. O medidor de força/checklist reaproveita
 * o mesmo componente/visual de `prototipo-configuracoes.html` (aba
 * Segurança) — ver `PasswordStrengthMeter`.
 */
export default function ChangePassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobileShell = useIsMobileShell();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const handleChangePassword = async (values) => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const response = await httpClient.post('/subscription/updatepassword', {
        new_password: values.newPassword,
        token: token
      });

      if (response.status === 200 || response.status === 204) {
        setSuccessMessage('Senha alterada com sucesso!');
        formik.resetForm();
        setTimeout(() => {
          navigate('/home');
        }, 2000);
      } else {
        const errorData = response.data || {};
        setErrorMessage(errorData);
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      const errorData = error.response?.data || {};
      setErrorMessage(errorData.message || 'Erro de conexão. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: ChangePasswordSchema,
    onSubmit: (values) => {
      handleChangePassword(values);
    },
  });

  const confirmMatches = formik.values.confirmPassword.length > 0 && !formik.errors.confirmPassword;

  if (isMobileShell) {
    return (
      <MobileChangePassword
        formik={formik}
        isLoading={isLoading}
        successMessage={successMessage}
        errorMessage={errorMessage}
        showNewPassword={showNewPassword}
        setShowNewPassword={setShowNewPassword}
        showConfirmPassword={showConfirmPassword}
        setShowConfirmPassword={setShowConfirmPassword}
        onBack={() => navigate('/login')}
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
        backgroundColor: 'background.default',
      }}
    >
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <Box sx={{ position: 'absolute', width: 560, height: 560, borderRadius: '50%', filter: 'blur(90px)', opacity: theme.palette.mode === 'dark' ? 0.28 : 0.5, backgroundColor: 'primary.main', top: -160, left: -120 }} />
        <Box sx={{ position: 'absolute', width: 480, height: 480, borderRadius: '50%', filter: 'blur(90px)', opacity: theme.palette.mode === 'dark' ? 0.28 : 0.5, backgroundColor: 'brand2.main', bottom: -140, right: -100 }} />
      </Box>

      <Box
        sx={{
          position: 'relative', zIndex: 1, width: '100%', maxWidth: 420,
          backgroundColor: alpha(theme.palette.background.paper, 0.75),
          backdropFilter: 'blur(24px) saturate(160%)',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '26px', boxShadow: theme.customShadows.lg,
          p: { xs: '28px 22px 26px', sm: '34px 32px 30px' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '18px', backgroundImage: theme.palette.gradient.brand, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, boxShadow: '0 10px 26px rgba(37,99,235,.35)' }}>
            <KeyRound size={28} />
          </Box>
          <Typography component="h1" sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', textAlign: 'center' }}>
            Redefinir senha
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', mt: 0.5 }}>
            Escolha uma nova senha para sua conta.
          </Typography>
        </Box>

        {successMessage && <Alert severity="success" sx={{ mb: 2.5 }}>{successMessage}</Alert>}
        {errorMessage && <Alert severity="error" sx={{ mb: 2.5 }}>{errorMessage}</Alert>}

        <Box component="form" onSubmit={formik.handleSubmit}>
          <TextField
            fullWidth
            variant="outlined"
            sx={standaloneFieldSx}
            name="newPassword"
            label="Nova senha"
            type={showNewPassword ? 'text' : 'password'}
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={!!(formik.touched.newPassword && formik.errors.newPassword)}
            helperText={formik.touched.newPassword && formik.errors.newPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="alternar visibilidade da senha" onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                    {showNewPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <PasswordStrengthMeter password={formik.values.newPassword} sx={{ mb: 2, mt: 1 }} />

          <TextField
            fullWidth
            variant="outlined"
            sx={standaloneFieldSx}
            name="confirmPassword"
            label="Confirmar nova senha"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={!!(formik.touched.confirmPassword && formik.errors.confirmPassword)}
            helperText={confirmMatches ? undefined : (formik.touched.confirmPassword && formik.errors.confirmPassword)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="alternar visibilidade da senha" onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                    {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{ height: 50, fontSize: 14, mt: 3 }}
          >
            {isLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Redefinir senha'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
